/**
 * Catálogo de los permisos que usa el sistema, agrupados como se muestran en el editor de roles.
 *
 * `description` es el texto exacto de la fila en la base (Permissions.Description) y el que piden
 * el servidor (`IsAutorized(...)`) y la web (`tienePermiso`/`isAccess`): no se cambia nunca.
 * Antes el editor solo mostraba 40 de estos 46: faltaban NÓMINA, "Ver el menú de Reportes" y
 * "Rastreo de gestores", que no se podían dar desde la web.
 *
 * `ayuda` aclara qué abre el permiso cuando el nombre no basta.
 */
export const GRUPOS_PERMISOS = [
  {
    titulo: "Clientes",
    permisos: [
      { description: "CLIENTS VER", label: "Ver clientes" },
      { description: "CLIENTS CREATE", label: "Crear clientes" },
      { description: "CLIENTS UPDATE", label: "Editar clientes" },
      { description: "CLIENTS DELETE", label: "Eliminar clientes" },
    ],
  },
  {
    titulo: "Créditos",
    permisos: [
      { description: "CREDITS VER", label: "Ver créditos" },
      { description: "CREDITS CREATE", label: "Crear créditos" },
      { description: "CREDITS UPDATE", label: "Editar créditos" },
      { description: "CREDITS REGEN", label: "Regenerar el plan de pagos" },
      { description: "CREDITS DELETE", label: "Eliminar créditos" },
      { description: "CREDIT LIQUIDATE", label: "Liquidar créditos" },
      { description: "PAYMENT UPDATE", label: "Cambiar la fecha de una cuota" },
      { description: "PRINT PAGARE", label: "Imprimir el pagaré" },
    ],
  },
  {
    titulo: "Nómina y empleados",
    permisos: [
      { description: "NOMINA VER", label: "Ver nóminas y empleados" },
      {
        description: "NOMINA CREAR",
        label: "Crear nóminas y empleados",
        ayuda: "También liquidar empleados",
      },
      { description: "NOMINA UPDATE", label: "Modificar nóminas y empleados" },
      { description: "NOMINA DELETE", label: "Desactivar empleados y borrar registros" },
    ],
  },
  {
    titulo: "Reportes",
    permisos: [
      { description: "REPORTS VER", label: "Ver el menú de Reportes" },
      { description: "RCLIENTS", label: "Clientes" },
      { description: "RCREDXGEST", label: "Créditos por gestor" },
      { description: "RCOBDIARIO", label: "Cobro diario" },
      { description: "RECUPERAC", label: "Recuperación" },
      { description: "RDESEMBOLSO", label: "Desembolsos" },
      {
        description: "RSALCART",
        label: "Saldos de cartera",
        ayuda: "También Saldos por gestor y Análisis de cartera",
      },
      {
        description: "RCREDVENC",
        label: "Créditos vencidos",
        ayuda: "También Bosque de mora y Recuperación de vencidos",
      },
      { description: "RVENCPROY", label: "Vencimientos proyectados" },
      { description: "RCREDCANC", label: "Créditos cancelados" },
      { description: "RACCOUNTSTS", label: "Estado de cuenta" },
      { description: "RBOXCLOSE", label: "Cierre de caja" },
      { description: "RRESULTSTS", label: "Estado de resultado" },
      { description: "REP UTRACK", label: "Rastreo de gestores" },
    ],
  },
  {
    titulo: "Seguridad",
    permisos: [
      { description: "SECURITY VER", label: "Ver usuarios y roles" },
      { description: "SECURITY CREATE", label: "Crear usuarios y roles" },
      {
        description: "SECURITY UPDATE",
        label: "Editar usuarios y roles",
        ayuda: "También restablecer contraseñas",
      },
      {
        description: "SECURITY DELETE",
        label: "Desactivar y reactivar usuarios, borrar roles",
      },
      { description: "VER HORARIOS", label: "Ver horarios de acceso" },
      { description: "EDITAR HORARIOS", label: "Editar horarios de acceso" },
    ],
  },
  {
    titulo: "Configuración",
    permisos: [
      { description: "CONFIG VER", label: "Ver configuración" },
      { description: "CONFIG CREATE", label: "Agregar en configuración" },
      { description: "CONFIG UPDATE", label: "Editar configuración" },
      { description: "CONFIG DELETE", label: "Eliminar en configuración" },
    ],
  },
  {
    titulo: "Caja y gastos",
    descripcion: "Pantallas que hoy no están en el menú de la web.",
    permisos: [
      { description: "CAJA VER", label: "Ver caja" },
      { description: "CAJA CREATE", label: "Registrar movimientos de caja" },
      { description: "CASHMOV VER", label: "Ver caja chica" },
      { description: "CASHMOV CREATE", label: "Registrar movimientos de caja chica" },
      { description: "ADMON VER", label: "Ver gastos" },
      { description: "ADMON CREATE", label: "Registrar gastos" },
    ],
  },
];

export const TODOS_LOS_PERMISOS = GRUPOS_PERMISOS.flatMap((g) => g.permisos.map((p) => p.description));

/**
 * Estado de las casillas a partir de las filas del rol: cada casilla es el `isEnable` de su fila
 * (nunca `isFullAccess`); si el rol no tiene la fila, va apagada.
 * @param {Array<{description: string, isEnable: boolean}>} filas
 * @returns {Object<string, boolean>}
 */
export const permisosDesdeFilas = (filas = []) =>
  Object.fromEntries(
    TODOS_LOS_PERMISOS.map((description) => [
      description,
      Boolean(filas.find((p) => p.description === description)?.isEnable),
    ])
  );

/** Lo que se manda al servidor: solo los permisos del catálogo (los que se muestran). */
export const filasParaServidor = (permisos) =>
  TODOS_LOS_PERMISOS.map((description) => ({ description, isEnable: Boolean(permisos[description]) }));
